import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useLocation,
  useNavigate,
  unstable_usePrompt as usePrompt,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useUnload from "../../hooks/useUnload";
import useSendContact from "../../hooks/useSendContact";
import useGetLocalePath from "../../hooks/useGetLocalePath";
import useOnClickOutside from "../../hooks/useOnClickOutside";

import {
  setBlur,
  setContactReason,
  setContactZIndex,
  setShowContactDialog,
} from "../../redux/reducers/uiReducer";

import { DevTool } from "@hookform/devtools";
import ReCAPTCHA from "react-google-recaptcha";
import { yupResolver } from "@hookform/resolvers/yup";
import { PhoneNumberUtil } from "google-libphonenumber";

import ContactForm from "../contact-form";
import getContactSchema from "../../schemas/contactSchema";
import { errorToast, successToast, warnToast } from "../toast";

import {
  isEmpty,
  isEmptyObject,
  equalsIgnoringCase,
} from "../../utils/validators";
import { logDebug, logError, logWarn } from "../../utils/logger";

function ContactDialog() {
  const { t } = useTranslation();

  const recaptchaRef = useRef(null);
  const contactDialogRef = useRef(null);
  const phoneUtil = PhoneNumberUtil.getInstance();

  const [isBlocked, setIsBlocked] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { getLocalePath } = useGetLocalePath();

  const dispatch = useDispatch();
  const { ui } = useSelector((state) => state);
  const { contact_z_index } = ui;

  const {
    data: dataSendContact,
    error: errorSendContact,
    loading: loadingSendContact,
    sendContact,
    clear: clearSendContact,
  } = useSendContact();

  const loadingForm = useMemo(() => {
    return [recaptchaLoading, loadingSendContact].some((i) => i);
  }, [recaptchaLoading, loadingSendContact]);

  const customValidators = useMemo(() => {
    return {
      tlf: (phone, context) => {
        try {
          if (!isEmpty(phone)) {
            const parsedPhone = phoneUtil.parseAndKeepRawInput(phone);
            return phoneUtil.isValidNumber(parsedPhone);
          }
          return true;
        } catch (error) {
          logError("ContactDialog", "customValidators", {
            context: context,
            value: phone,
            error: error,
          });
          return false;
        }
      },
    };
  }, [phoneUtil]);

  const defaultValues = useMemo(() => {
    return {
      name: "",
      email: "",
      email2: "",
      tlf: "",
      reason: null,
      message: "",
      consent: false,
    };
  }, []);

  const zIndex = useMemo(() => {
    return !isEmpty(contact_z_index) ? parseInt(contact_z_index) : 101;
  }, [contact_z_index]);

  const form = useForm({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    disabled: loadingForm,
    defaultValues: defaultValues,
    resolver: yupResolver(getContactSchema(t, customValidators)),
  });
  const { control, formState, reset } = form;
  const { isDirty, disabled } = formState;

  useUnload({
    when: isBlocked,
    message: t("message.askConfirmation"),
  });

  usePrompt({
    when: isBlocked,
    message: t("message.askConfirmation"),
  });

  useOnClickOutside(contactDialogRef, (evt) => {
    logDebug("ContactDialog", "useOnClickOutside", {
      evt: evt,
    });
    if (equalsIgnoringCase(evt.target.className, "wrapper")) {
      closeDialogHandler(evt);
    }
  });

  const closeDialogHandler = useCallback(
    (evt) => {
      logDebug("ContactDialog", "closeDialogHandler", {
        evt: evt,
      });
      if (isBlocked) {
        if (window.confirm(t("message.askConfirmation"))) {
          setIsBlocked(false);
          setTimeout(() => {
            if (
              equalsIgnoringCase(
                getLocalePath(t("routes.public.contact")),
                location.pathname
              )
            ) {
              navigate(getLocalePath(t("routes.common.home")), {
                state: { targetId: "" },
              });
            }
          }, 100);
          setTimeout(() => {
            dispatch(setContactReason(""));
            dispatch(setContactZIndex(101));
            dispatch(setBlur(false));
            dispatch(setShowContactDialog(false));
          }, 200);
        }
      } else {
        if (
          equalsIgnoringCase(
            getLocalePath(t("routes.public.contact")),
            location.pathname
          )
        ) {
          navigate(getLocalePath(t("routes.common.home")), {
            state: { targetId: "" },
          });
        }
        dispatch(setContactReason(""));
        dispatch(setBlur(false));
        dispatch(setContactZIndex(101));
        dispatch(setShowContactDialog(false));
      }
    },
    [isBlocked, location.pathname, t, getLocalePath, dispatch, navigate]
  );

  const onClickCloseDialogHandler = useCallback(
    (evt) => {
      logDebug("ContactDialog", "onClickCloseDialogHandler", {
        evt: evt,
      });
      closeDialogHandler(evt);
    },
    [closeDialogHandler]
  );

  const onInvalidHandler = useCallback(
    (errors, evt) => {
      const handler = async (errors, evt) => {
        logDebug("ContactDialog", "onInvalidHandler", {
          errors: errors,
          evt: evt,
        });
        errorToast(
          "ContactDialog",
          errors,
          t("errors.error_form", t("errors.default"))
        );
      };
      handler(errors, evt);
    },
    [t]
  );

  const onValidHandler = useCallback(
    (data, evt) => {
      const handler = async (data, evt) => {
        if (!disabled) {
          logDebug("ContactDialog", "onValidHandler", { data: data, evt: evt });
          if (!isEmptyObject(data)) {
            const recaptcha = await recaptchaRef.current.executeAsync();
            if (!isEmpty(recaptcha)) {
              sendContact({
                ...data,
                reason:
                  !isEmptyObject(data?.reason?.label) &&
                  !isEmpty(data.reason.label)
                    ? data.reason.label
                    : "",
                "g-recaptcha-response": recaptcha,
                url: window.location.href,
                resolution: `${window.innerWidth}x${window.innerHeight}`,
              });
            } else {
              logError("ContactDialog", "onValidHandler", {
                error: "invalid_recaptcha",
                msg: t("errors.invalid_recaptcha", t("errors.default")),
              });
              errorToast(
                "ContactDialog",
                "invalid_recaptcha",
                t("errors.invalid_recaptcha", t("errors.default"))
              );
              recaptchaRef.current.reset();
            }
          }
        }
      };
      handler(data, evt);
    },
    [disabled, sendContact, t]
  );

  const onErroredReCaptchaHandler = useCallback(
    (evt) => {
      logError("ContactDialog", "onErroredReCaptchaHandler", {
        evt: evt,
        code: "error_recaptcha",
        msg: t(`errors.error_recaptcha`, t("errors.default")),
      });
      errorToast(
        "ContactDialog",
        "error_recaptcha",
        t(`errors.error_recaptcha`, t("errors.default"))
      );
      setRecaptchaLoading(false);
      recaptchaRef.current.reset();
    },
    [t]
  );

  const onExpiredReCaptchaHandler = useCallback(
    (evt) => {
      logWarn("ContactDialog", "onExpiredReCaptchaHandler", {
        evt: evt,
        code: "expired_recaptcha",
        msg: t(`validators.expired_recaptcha`, t("errors.default")),
      });
      warnToast(
        "ContactDialog",
        "expired_recaptcha",
        t(`validators.expired_recaptcha`, t("errors.default"))
      );
      setRecaptchaLoading(false);
      recaptchaRef.current.reset();
    },
    [t]
  );

  const asyncScriptOnLoadReCaptchaHandler = useCallback((evt) => {
    logDebug("ContactDialog", "asyncScriptOnLoadReCaptchaHandler", {
      evt: evt,
    });
  }, []);

  const onConsentClickHandler = useCallback(
    (evt, type) => {
      logDebug("ContactDialog", "onConsentClickHandler", {
        evt: evt,
        type: type,
      });
      evt.preventDefault();
      evt.stopPropagation();
      if (equalsIgnoringCase(type, "LEGAL")) {
        window.open(
          `https://${process.env.REACT_APP_DOMAIN}${getLocalePath(
            t("routes.public.legal")
          )}`,
          "_blank"
        );
      } else if (equalsIgnoringCase(type, "PRIVACY")) {
        window.open(
          `https://${process.env.REACT_APP_DOMAIN}${getLocalePath(
            t("routes.public.privacy")
          )}`,
          "_blank"
        );
      } else if (equalsIgnoringCase(type, "COOKIES")) {
        window.open(
          `https://${process.env.REACT_APP_DOMAIN}${getLocalePath(
            t("routes.public.cookies")
          )}`,
          "_blank"
        );
      }
    },
    [t, getLocalePath]
  );

  const onChangeDefaultValuesHandler = useCallback(
    (evt) => {
      logDebug("ContactDialog", "onChangeDefaultValuesHandler", {
        evt: evt,
      });
      reset(evt);
    },
    [reset]
  );

  useEffect(() => {
    setIsBlocked(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!loadingSendContact) {
      if (!isEmpty(errorSendContact)) {
        logError("ContactDialog", "useSendContact", {
          error: errorSendContact,
          msg: t(`errors.${errorSendContact}`, t(`errors.default`)),
        });
        errorToast(
          "ContactDialog - useSendContact",
          errorSendContact,
          t(`errors.${errorSendContact}`, t("errors.default"))
        );
        clearSendContact();
        recaptchaRef.current.reset();
      } else if (!isEmptyObject(dataSendContact)) {
        logDebug("ContactDialog", "useSendContact", {
          code: "registered_request",
          msg: t(`message.registered_request`, t("message.default")),
        });
        successToast(
          "ContactDialog - useSendContact",
          "registered_request",
          t(`message.registered_request`, t("message.default"))
        );
        reset(defaultValues);
        recaptchaRef.current.reset();
        setIsBlocked(false);
        setTimeout(() => {
          dispatch(setContactReason(""));
          dispatch(setContactZIndex(101));
          dispatch(setBlur(false));
          dispatch(setShowContactDialog(false));
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSendContact, dataSendContact, errorSendContact]);

  return (
    <>
      <div className="wrapper" style={{ zIndex: zIndex }}></div>
      <div
        ref={contactDialogRef}
        className="contact-form-dialog"
        style={{ zIndex: zIndex + 1 }}
      >
        <div className="contact-form-dialog__header">
          <span className="header-3">{t("contact.title")}</span>
          <span className="title">{t("contact.subtitle")}</span>
          <span
            className="icon-close"
            onClick={(evt) => onClickCloseDialogHandler(evt)}
          ></span>
        </div>
        <div className="contact-form-dialog__body">
          <ContactForm
            form={form}
            loading={loadingForm}
            onValid={(data, evt) => onValidHandler(data, evt)}
            onInvalid={(errors, evt) => onInvalidHandler(errors, evt)}
            onConsentClick={(evt, type) => onConsentClickHandler(evt, type)}
            onChangeDefaultValues={(evt) => onChangeDefaultValuesHandler(evt)}
          />
        </div>
      </div>
      <ReCAPTCHA
        ref={recaptchaRef}
        className="contact-reCAPTCHA"
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
        badge="bottomright"
        size="invisible"
        onErrored={(evt) => onErroredReCaptchaHandler(evt)}
        onExpired={(evt) => onExpiredReCaptchaHandler(evt)}
        asyncScriptOnLoad={(evt) => asyncScriptOnLoadReCaptchaHandler(evt)}
      />
      <DevTool id="contact-dialog__devtools" control={control} />
    </>
  );
}

export default ContactDialog;
